import {CardComponent, CardNumber, CardExpiry, CardCVV} from '@chargebee/chargebee-js-react-wrapper';
import cx from 'classnames';
import {isEmpty} from 'lodash';
import {Fragment, useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {StepWizardChildProps} from 'react-step-wizard';

import {DataWrapper, WizardFooter} from 'components';
import {BillingCardFieldName, ValidationCode} from 'enums';
import {useApiData, useLanguage} from 'hooks';
import {TRANSLATION_KEYS} from 'locale';
import {BillingPlatformChangeEvent, WizardFooterProps} from 'types';
import {apiUtils} from 'utils';

import {ConfirmPaymentModal} from './ConfirmPaymentModal';

const T_KEYS_COMMON = TRANSLATION_KEYS.COMMON;
const T_KEYS = TRANSLATION_KEYS.COMPONENTS.CHARGEBEE_PAYMENT;

const CARD_STYLES = {
    base: {
        fontSize: '13px',
        '::placeholder': {
            color: '#9BACC8',
        }
    }
};

const CARD_CLASSES = {
    invalid: 'invalid'
};

const CARD_INPUT_CLASSES = 'tw-w-full tw-pt-1 tw-mt-4 tw-h-8 vs-form-control field stateful-field tw-bg-light';

type Props = {
    credentials: {site: string, publishableKey: string},
    maxUsers?: number;
    unitPrice?: number;
    currency?: string;
    periodUnit?: string;
    noImmediateCharge?: boolean;
    usersToRemove?: string[];
    wizardFooterProps: WizardFooterProps;
    paymentAddedCallback: (data: {paymentToken: string, maxUsers: number, usersToRemove: string[]}) => Promise<void>;
} & Partial<StepWizardChildProps>

export const ChargebeePaymentStep: React.FC<Props> = ({
    maxUsers, unitPrice, currency, periodUnit, credentials, nextStep, previousStep,
    paymentAddedCallback, noImmediateCharge, usersToRemove, wizardFooterProps
}) => {
    const {t} = useTranslation();
    const [language] = useLanguage();
    const cardRef = useRef(null);
    const [tokenizeError, setTokenizeError] = useState(null);
    const [chargebeeLoaded, setChargebeeLoaded] = useState(false);
    const [paymentToken, setPaymentToken] = useState<string>(null);
    const [confirmPaymentModalProps, setConfirmPaymentModalProps] = useState<{
        open: boolean,
        maxUsers?: number,
        unitPrice?: number;
        currency?: string;
        periodUnit?: string;
    }>({
        open: false
    });

    const {
        send: sendPayment,
        error: paymentError,
        loading: paymentLoading,
        reset: resetPayment
    } = useApiData(paymentAddedCallback, {
        onSuccess: nextStep
    });

    const [cardValidationState, setCardValidationState] = useState<Record<BillingCardFieldName, boolean>>(null);
    const [tokenizeInProgress, setTokenizeInProgress] = useState(false);

    const isFormValid = useMemo(() =>
        cardValidationState?.number && cardValidationState?.expiry && cardValidationState?.cvv, [cardValidationState]);

    const handleConfirmModalClose = useCallback((confirm: boolean) => {
        if (confirm) {
            sendPayment({maxUsers, paymentToken, usersToRemove});
        }
        setConfirmPaymentModalProps({open: false});
    }, [sendPayment, maxUsers, paymentToken, usersToRemove]);

    const handleNext = useCallback(() => {
        setTokenizeError(null);
        setTokenizeInProgress(true);
        cardRef.current.tokenize()
            .then((data: {token: string, vaultToken: string}) => {
                setPaymentToken(data.token);
                if (noImmediateCharge) {
                    sendPayment({maxUsers, paymentToken: data.token, usersToRemove});
                } else {
                    setConfirmPaymentModalProps({
                        open: true,
                        maxUsers,
                        unitPrice,
                        currency,
                        periodUnit
                    });
                }
            })
            .catch((error) => {
                setTokenizeError(error);
            })
            .finally(() => {
                setTokenizeInProgress(false);
            });

    }, [noImmediateCharge, usersToRemove, sendPayment, maxUsers, unitPrice, currency, periodUnit]);

    const handleCardChange = useCallback((data: BillingPlatformChangeEvent) => {
        if (paymentError) {
            resetPayment();
        }
        if (tokenizeError) {
            setTokenizeError(null);
        }

        setCardValidationState((prev) => ({
            ...prev,
            [data.field]: data.complete && isEmpty(data.error)
        }));
    }, [paymentError, tokenizeError, resetPayment]);

    useEffect(() => {
        if (!isEmpty(credentials) && !chargebeeLoaded) {
            (window as any).Chargebee.init(credentials);
            setChargebeeLoaded(true);
        }

    }, [credentials, chargebeeLoaded]);

    const paymentErrorMessage = useMemo(() => paymentError ? apiUtils.getFieldValidationErrorMessage(paymentError, t, 'card', {
        [ValidationCode.Duplicated]: T_KEYS.ERROR.DUPLICATED
    }) || t(T_KEYS.ERROR.UNEXPECTED) : null, [paymentError, t]);

    return (
        <Fragment>
            <DataWrapper isLoading={!chargebeeLoaded || paymentLoading || tokenizeInProgress} hideContentOnLoading={false}>
                <div className='tw-flex tw-flex-col tw-items-center'>
                    <div className='tw-mb-8 tw-text-h3'>{t(T_KEYS.TITLE)}</div>
                    {paymentErrorMessage &&
                        <div className='tw-text-alert tw-ts-h5-bold tw-text-center tw-mb-8'>{paymentErrorMessage}</div>}
                    {tokenizeError &&
                        <div className='tw-text-alert tw-ts-h5-bold tw-text-center tw-mb-8'>{t(T_KEYS.ERROR.SERVICE_DOWN)}</div>}
                    <div className={cx('tw-border-2 tw-border-dashed tw-rounded-2xl tw-py-8 tw-h-52 tw-w-80', {
                        'tw-border-alert': paymentError,
                        'tw-border-default': !paymentError
                    })}
                    >
                        <div className='tw-bg-default tw-h-12' />
                        {chargebeeLoaded &&
                            <div className='tw-px-4'>
                                <CardComponent
                                    ref={cardRef}
                                    locale={language}
                                    styles={CARD_STYLES}
                                    classes={CARD_CLASSES}
                                    onChange={handleCardChange}
                                >
                                    <CardNumber
                                        placeholder={t(T_KEYS_COMMON.CREDIT_CARD.NUMBER)}
                                        className={CARD_INPUT_CLASSES}
                                    />
                                    <div className='tw-grid tw-grid-cols-2 tw-gap-4'>
                                        <CardExpiry
                                            placeholder={t(T_KEYS_COMMON.CREDIT_CARD.EXPIRE)}
                                            className={CARD_INPUT_CLASSES}
                                        />
                                        <CardCVV placeholder={t(T_KEYS_COMMON.CREDIT_CARD.CVV)} className={CARD_INPUT_CLASSES} />
                                    </div>
                                </CardComponent>
                            </div>}
                    </div>
                </div>
                <WizardFooter
                    {...wizardFooterProps}
                    disabledNext={wizardFooterProps.disabledNext || !isFormValid || tokenizeInProgress || paymentLoading}
                    handleNext={handleNext}
                    handlePrevious={previousStep}
                />
            </DataWrapper>
            <ConfirmPaymentModal {...confirmPaymentModalProps} onClose={handleConfirmModalClose} />
        </Fragment>
    );
};